import React, { useState, useEffect, useRef } from "react";
import { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { cloneDeep } from "lodash";
import { useTranslation } from "react-i18next";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { ProgressSpinner } from "primereact/progressspinner";
import { DropdownInput } from "../../components/DropdownInput/DropdownInput";
import { DocumentOfReceive } from "../../models/DocumentOfReceive";
import {
  OwnershipDocument,
  CombinedDocument,
} from "../../models/OwnershipDocument";
import { Partner } from "../../models/Partner";
import { Warehouse } from "../../models/Warehouse";
import { RoleType } from "../../models/Role";
import { Product } from "../../models/Product";
import { PageLayout } from "../../layout/PageLayout/PageLayout";
import { activeUser } from "../../services/AuthService";
import { getAllWarehouses } from "../../services/WarehouseService";
import { getAllPartners } from "../../services/PartnerService";
import { getAllProducts } from "../../services/ProductService";
import { getAllForeignProductDocuments } from "../../services/NewVersionService";
import { getAllOwnershipDocuments } from "../../services/NewVersionService";
import { useApplicationContext } from "../../application/ApplicationContext";
import { Utility } from "../../core/Utility";
import "./ForeignReproProductPage.scss";

export const ForeignReproProductsPage = () => {
  const { t } = useTranslation();

  const { state } = useApplicationContext();
  const businessYear = state.businessYear ?? new Date().getFullYear();

  const [processing, setProcessing] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse>();
  const [selectedPartner, setSelectedPartner] = useState<Partner>(
    {} as Partner,
  );

  const [allReproInputDocuments, setAllReproInputDocuments] = useState<
    CombinedDocument[]
  >([]);
  const [reproInputDocuments, setReproInputDocuments] = useState<
    CombinedDocument[]
  >([]);
  const [filteredInputDocuments, setFilteredInputDocuments] = useState<
    CombinedDocument[]
  >([]);

  const [warehousesApi, setWarehousesApi] = useState<Warehouse[]>([]);
  const [partnersApi, setPartnersApi] = useState<Partner[]>([]);
  const [productsApi, setProductsApi] = useState<Product[]>([]);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  const refreshLookups = async () => {
    const [wResp, partnerResp, pResp] = await Promise.all([
      getAllWarehouses(),
      getAllPartners(),
      getAllProducts(),
    ]);

    [wResp, partnerResp, pResp].forEach(ensureSuccess);

    const wh = wResp?.data?.result ?? [];
    const pa = partnerResp?.data?.result ?? [];
    const pr = pResp?.data?.result ?? [];

    setWarehousesApi(wh);
    setPartnersApi(pa);
    setProductsApi(pr);

    return { wh, pa, pr };
  };

  useEffect(() => {
    const run = async () => {
      setProcessing(true);
      try {
        const { pr } = await refreshLookups();
        const productById = new Map<string, Product>();
        pr.forEach((p: any) => productById.set(String(p.id), p));

        const [foreignDocsResponse, ownershipDocsResponse] = await Promise.all([
          getAllForeignProductDocuments(false),
          getAllOwnershipDocuments(),
        ]);

        const allDocuments = foreignDocsResponse.data?.result ?? [];
        const allOwnershipDocuments = ownershipDocsResponse.data?.result ?? [];

        const allReproOwnershipDocuments = allOwnershipDocuments.filter(
          (one: OwnershipDocument) => one.mercantile === false,
        );

        const parseReproProducts = (json?: string) => {
          if (!json) return [];
          try {
            const parsed = JSON.parse(json);
            return (parsed ?? []).map((productData: any) => {
              const fullProduct = productById.get(String(productData.id));
              return {
                id: productData.id,
                name: fullProduct?.name ?? String(productData.id),
                amount: productData.amount,
                measuringUnit:
                  productData.measuringUnit ?? fullProduct?.measuringUnit,
                category: fullProduct?.category,
              };
            });
          } catch (e) {
            console.log("Error parsing repromaterialProductsJSON:", e);
            return [];
          }
        };

        let combinedDocuments: CombinedDocument[] = [
          ...allDocuments.map((doc: DocumentOfReceive) => {
            const repromaterialProducts = parseReproProducts(
              doc.repromaterialProductsJSON,
            );

            return {
              id: doc.id,
              documentType: doc.input ? "PRIJEMNICA" : "OTPREMNICA",
              documentId: doc?.finishedDocumentNumber ?? doc.documentId,
              finishedDocumentId:
                doc?.finishedDocumentNumber ?? doc.finishedDocumentId,
              date: new Date(doc.date as Date),
              warehouse: { ...doc.warehouse },
              warehouseName: doc.warehouse?.name,
              loadingCityName: doc.loadingCityName,
              partner: doc.input ? { ...doc.partner } : { ...doc.productOwner },
              input: doc.input,
              repromaterialProducts,
              reproProducts: repromaterialProducts
                .map((p: any) => p.id)
                .join(";"),
              reproAmounts: repromaterialProducts
                .map((p: any) => `${p.amount}${p.measuringUnit}`)
                .join(";"),
            };
          }),
          ...allReproOwnershipDocuments.map((doc: OwnershipDocument) => {
            const repromaterialProducts = parseReproProducts(
              doc.repromaterialProductsJSON,
            );

            return {
              id: doc.id,
              documentType: "VLASNICKI_LIST",
              documentId: doc?.ownerDocNumberFormatted ?? doc.ownerDocId,
              ownerDocId: doc?.ownerDocNumberFormatted ?? doc.ownerDocId,
              date: new Date(doc.date),
              warehouse: { ...doc.warehouse },
              warehouseName: doc.warehouse?.name,
              previousOwner: { ...doc.previousOwner },
              newOwner: { ...doc.newOwner },
              repromaterialProducts,
              reproProducts: repromaterialProducts
                .map((p: any) => p.id)
                .join(";"),
              reproAmounts: repromaterialProducts
                .map((p: any) => `${p.amount}${p.measuringUnit}`)
                .join(";"),
              repromaterialProductsJSON: doc.repromaterialProductsJSON,
            };
          }),
        ];

        combinedDocuments = combinedDocuments
          .filter(
            (doc) => new Date(doc.date as Date).getFullYear() === businessYear,
          )
          .sort((a, b) => b.date.getTime() - a.date.getTime());

        setAllReproInputDocuments(combinedDocuments);
        setFilteredInputDocuments(combinedDocuments);
        formatReproDocumentsData(combinedDocuments);
      } catch (error: any) {
        console.log(error);
      } finally {
        setProcessing(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessYear]);

  const toast = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    formatReproDocumentsData(allReproInputDocuments);
  }, [selectedWarehouse, selectedPartner]);

  const formatReproDocumentsData = (allDocuments: CombinedDocument[]) => {
    let copyOfReproDocuments: CombinedDocument[] = [];

    for (const reproInput of allDocuments) {
      const warehouseMatch = selectedWarehouse?.id
        ? reproInput.warehouse?.id === selectedWarehouse.id
        : true;

      const partnerMatch = selectedPartner?.id
        ? reproInput.documentType === "VLASNICKI_LIST"
          ? reproInput.previousOwner?.id === selectedPartner.id
          : reproInput.partner?.id === selectedPartner.id
        : true;

      if (warehouseMatch && partnerMatch) {
        copyOfReproDocuments.push(reproInput);
      }
    }

    copyOfReproDocuments.sort((a: CombinedDocument, b: CombinedDocument) => {
      return (a.date as Date) < (b.date as Date) ? 1 : -1;
    });
    setReproInputDocuments(copyOfReproDocuments);
  };

  const handleWarehouseChange = (prop: string, value: any) => {
    let copyOfNewWarehouse = cloneDeep(selectedWarehouse);
    copyOfNewWarehouse = warehousesApi.find(
      (item) => item.id == value,
    ) as Warehouse;
    setSelectedWarehouse(copyOfNewWarehouse);
  };

  const handlePartnerChange = (prop: string, value: any) => {
    let copyOfNewPartner = cloneDeep(selectedPartner);
    copyOfNewPartner = partnersApi.find((item) => item.id == value) as Partner;
    setSelectedPartner(copyOfNewPartner);
  };

  const {
    control,
    formState: { errors },
    reset,
    setValue,
  } = useForm({
    mode: "all",
  });

  const formatFinishedDocumentId = (rowData: CombinedDocument) => {
    if (rowData.documentType === "VLASNICKI_LIST") {
      return rowData.ownerDocId ?? "/";
    }
    return rowData?.finishedDocumentId ?? rowData?.documentId ?? "/";
  };

  const formatDocumentType = (rowData: CombinedDocument) => {
    switch (rowData.documentType) {
      case "PRIJEMNICA":
        return "Prijemnica";
      case "OTPREMNICA":
        return "Otpremnica";
      case "VLASNICKI_LIST":
        return "Vlasnički list";
      default:
        return "/";
    }
  };

  const formatPartnerName = (rowData: CombinedDocument) => {
    if (rowData.documentType === "VLASNICKI_LIST") {
      return rowData.previousOwner?.name; // Za vlasničke listove prikazujemo prethodnog vlasnika
    }
    return rowData.partner?.name; // Za prijemnice/otpremnice
  };

  const formatDate = (rowData: CombinedDocument) => {
    return Utility.formatDate(new Date(rowData.date as Date));
  };

  const formatProducts = (rowData: CombinedDocument) => {
    if (
      !rowData.repromaterialProducts ||
      rowData.repromaterialProducts.length === 0
    ) {
      return "/";
    }

    return (
      <div style={{ whiteSpace: "pre-line" }}>
        {rowData.repromaterialProducts
          .map((product) => product.name)
          .join("\n")}
      </div>
    );
  };

  const formatAmounts = (rowData: CombinedDocument) => {
    if (
      !rowData.repromaterialProducts ||
      rowData.repromaterialProducts.length === 0
    ) {
      return "/";
    }

    return (
      <div style={{ whiteSpace: "pre-line" }}>
        {rowData.repromaterialProducts
          .map((product) => `${product.amount} ${product.measuringUnit}`)
          .join("\n")}
      </div>
    );
  };

  const formatOptions = (rowData: CombinedDocument) => {
    return (
      <div className="action-buttons">
        <ButtonInput
          addStyle={{ border: "none", background: "#198754" }}
          labelText={""}
          icon="bi bi-eye"
          onButtonClick={() => {
            const id = rowData.id;
            if (rowData.documentType === "PRIJEMNICA") {
              navigate(`/document-of-receive-repromaterial-preview-fast/${id}`);
            } else if (rowData.documentType === "OTPREMNICA") {
              navigate(`/document-of-output-repromaterial-preview-fast/${id}`);
            } else {
              navigate(`/ownership-repro-document/${id}`);
            }
          }}
        />
      </div>
    );
  };

  const exportToExcel = () => {
    const excelData = filteredInputDocuments.flatMap(
      (item: CombinedDocument) => {
        if (
          !item.repromaterialProducts ||
          item.repromaterialProducts.length === 0
        ) {
          return [
            {
              BrojDokumenta:
                item.documentType === "VLASNICKI_LIST"
                  ? (item.ownerDocId ?? "/")
                  : (item?.finishedDocumentId ?? item?.documentId ?? "/"),
              Tip: item.documentType,
              Datum: Utility.formatDate(new Date(item.date as Date)),
              Magacin: item.warehouseName,
              Dobavljac:
                item.documentType === "VLASNICKI_LIST"
                  ? item.previousOwner?.name
                  : item.partner?.name,
              Proizvod: "/",
              Količina: 0,
              MernaJedinica: "/",
            },
          ];
        }

        return item.repromaterialProducts.map((product) => ({
          BrojDokumenta:
            item.documentType === "VLASNICKI_LIST"
              ? (item.ownerDocId ?? "/")
              : (item?.finishedDocumentId ?? item?.documentId ?? "/"),
          Tip: item.documentType,
          Datum: Utility.formatDate(new Date(item.date as Date)),
          Magacin: item.warehouseName,
          Dobavljac:
            item.documentType === "VLASNICKI_LIST"
              ? item.previousOwner?.name
              : item.partner?.name,
          Proizvod: product.name,
          Količina: parseFloat(product.amount.toString()) || 0,
          MernaJedinica: product.measuringUnit,
        }));
      },
    );

    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelData);
      // Postavi format za numeričke ćelije (Količina kolona)
      if (worksheet["!ref"]) {
        const range = xlsx.utils.decode_range(worksheet["!ref"]);
        for (let i = range.s.r + 1; i <= range.e.r; i++) {
          const cellAddress = xlsx.utils.encode_cell({ r: i, c: 5 }); // Column F (Količina) - index 5
          if (
            worksheet[cellAddress] &&
            typeof worksheet[cellAddress].v === "number"
          ) {
            worksheet[cellAddress].t = "n"; // Set cell type to numeric
            worksheet[cellAddress].z = "0.00"; // Format sa dve decimale
          }
        }
      }

      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(
        excelBuffer,
        `Tudja repromaterijal roba na lageru ${Utility.formatDate(new Date())}`,
      );
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  return (
    <PageLayout>
      <Toast ref={toast} position={"top-right"} />
      <div className="table-wrapper">
        <div className="table-header">
          <div className="header-left">
            <i
              className="bi bi-boxes header-title-icon"
              style={{
                fontSize: 14,
                marginLeft: "1rem",
              }}
            ></i>
            <span className="header-left-title">
              {t("Tudja repromaterijal roba na lageru")}
            </span>
            <span className="header-elements-text">{`${t("u")}`}</span>
            <DropdownInput
              id="warehouse"
              value={selectedWarehouse?.id}
              onValueChange={handleWarehouseChange}
              optionValue="id"
              optionLabel="name"
              options={warehousesApi.filter((item) => item.virtual !== true)}
              labelText=""
              placeholderText={t("Magacin")}
              control={control}
              setValue={setValue}
              errors={errors}
              disabled={
                activeUser?.role?.name === RoleType.Warehouseman || processing
              }
            />
            <span className="header-elements">vlasnik</span>
            <DropdownInput
              id="selectedPartner"
              value={selectedPartner?.id}
              onValueChange={handlePartnerChange}
              optionValue="id"
              optionLabel="name"
              options={partnersApi}
              filter={true}
              labelText=""
              placeholderText={t("Vlasnik")}
              control={control}
              setValue={setValue}
              errors={errors}
              rules={{ required: true }}
            />
            <ButtonInput
              addStyle={{
                border: "none",
                background: "#ffffff",
                color: "#6C757D",
                margin: "0 0 0 1rem",
              }}
              labelText={"reset"}
              icon="bi bi-arrow-repeat"
              onButtonClick={() => {
                setSelectedWarehouse({} as Warehouse);
                setSelectedPartner({} as Partner);
              }}
              disabled={processing}
            />
          </div>
          <div className="header-right">
            <ButtonInput
              addStyle={{
                border: "1px solid #01a453",
                background: "#ffffff",
                color: "#01a453",
                marginLeft: "5px",
              }}
              labelText={""}
              icon="bi bi-file-earmark-excel"
              onButtonClick={exportToExcel}
              disabled={processing === true}
            />
          </div>
        </div>
        {processing ? (
          <ProgressSpinner />
        ) : (
          <div className="table-body">
            <DataTable
              scrollable={true}
              scrollHeight="80vh"
              sortMode="single"
              removableSort
              emptyMessage="Nema tudje robe na lageru za zadate kriterijume"
              value={reproInputDocuments}
              onValueChange={(filteredData) =>
                setFilteredInputDocuments(filteredData)
              }
              showGridlines
              stripedRows
            >
              <Column
                frozen
                body={formatFinishedDocumentId}
                header={t("Br.Dok.")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatDocumentType}
                header={t("Tip")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatDate}
                header={t("Datum")}
                style={{ minWidth: "80px" }}
              />
              <Column
                field="warehouseName"
                header={t("Magacin")}
                style={{ minWidth: "90px" }}
              />
              <Column
                body={formatPartnerName}
                header={t("Vlasnik")}
                style={{ minWidth: "100px" }}
              />
              <Column
                body={formatProducts}
                header={t("Proizvodi")}
                style={{ minWidth: "150px" }}
              />
              <Column
                body={formatAmounts}
                header={t("Količina")}
                style={{ minWidth: "120px" }}
              />
              <Column
                body={formatOptions}
                header={t("Opcije")}
                style={{ minWidth: "150px" }}
              />
            </DataTable>
          </div>
        )}
      </div>
    </PageLayout>
  );
};
